/**
 * @module builder
 */
import type { QuoteDocument, AreaSubstrate, OrderItem, AreaSubstrates, OptionsDocument } from 'paintscout'
import type { QuoteItemSection } from '../../index'
import { getItemSection } from '../../index'
import { moveAreaSubstrate } from '../../index'
// import { moveArea } from '../../index'
import { calculateQuote } from '../../util'
import get from 'lodash/get'

// Moving a substrate moves the associated areaSubstrates(substrateKey === areaSubstrateKey)
// that are ALSO in the prevSection, to the new section.
// Note: For any area we move a(n) areaSubstrate(s) in, we need to regenerate the
// areas substrate_string and totals as we do when moving an area.
// Substrates do not track sections any longer, they are just rebuilt after
// all related areaSubstrates are moved (in calculateQuote)
export function moveSubstrate(args: {
  quote: QuoteDocument
  options: OptionsDocument
  key: string
  section: QuoteItemSection
  previousSection: QuoteItemSection
  calculate?: boolean
  force?: boolean
}): QuoteDocument {
  const { key: substrateKey, section, previousSection, quote, options, calculate } = args

  // Return if the substrate doesn't exist
  if (!quote.substrates[substrateKey]) {
    return { ...quote }
  }

  let updatedQuote = {
    ...quote
  }

  // Check each areas areaSubstrates, add area to updatedQuote,
  // update area before adding if we end up changing an areaSubstrate
  updatedQuote.order.area.forEach((orderItem: OrderItem) => {
    if (orderItem.type !== 'area') {
      return
    }
    if (orderItem.parent) {
      return
    }
    if (orderItem._deleted) {
      return
    }

    const areaKey = orderItem.key
    const areaSubstrates: AreaSubstrates = {}
    let area = updatedQuote.areas[areaKey]
    let changedAreaSubstrate = false

    const areaSection = getItemSection(area)

    let areaHasSubstrate = false

    Object.keys(area.substrates).forEach((rateSection: string) => {
      area.substrates[rateSection].forEach((substrate: AreaSubstrate) => {
        const substrateSection = getItemSection(substrate)

        let areaSubstrateKey: string
        if (quote && quote.substratesGroupBy === 'client_label') {
          areaSubstrateKey = get(substrate, 'rate.client_label')
          if (!areaSubstrateKey) {
            areaSubstrateKey = get(substrate, 'rate.label')
          }
          if (!areaSubstrateKey) {
            areaSubstrateKey = 'Custom'
          }

          areaSubstrateKey = areaSubstrateKey.trim().toLowerCase()
        } else {
          areaSubstrateKey = substrate.rate.name === 'custom' ? substrate.key : substrate.rate.name
        }

        const isMoveCandidate =
          (substrateSection === 'bid' && areaSection === previousSection) || substrateSection === previousSection

        if (isMoveCandidate && areaSubstrateKey === substrateKey) {
          // This is the substrate in question - it needs to go to the specified section.

          updatedQuote = moveAreaSubstrate({ quote: updatedQuote, options, areaKey, key: substrate.key, section })
          areaHasSubstrate = true
        } else if (substrateSection === 'bid') {
          // This is a different substrate that was in the "bid"
          // We need to move it to the previous section, as we're doing this flip-flop
          // between the area section & the substrate section
          updatedQuote = moveAreaSubstrate({
            quote: updatedQuote,
            options,
            areaKey,
            key: substrate.key,
            section: areaSection
          })
        }
      })
    })

    // if (section === 'bid' && areaSection !== section) {
    //   updatedQuote = moveArea({ quote: updatedQuote, key: areaKey, section, calculate: false, previousSection })
    // }
    // if (areaHasSubstrate) {
    // updatedQuote = moveArea({
    // quote: updatedQuote,
    // options,
    // key: areaKey,
    // section: 'bid',
    // calculate: false,
    // previousSection
    // })
    // }
  })

  // Substrates will be rebuilt in calculateQuote
  return calculate ? calculateQuote({ quote: updatedQuote, options }) : updatedQuote
}
